import { Box, Typography } from "@mui/material";

import { t } from "i18next";
import { type FC, useCallback } from "react";
import { useNavigate } from "react-router";
import { Carousel } from "../discover/carousel/carousel";

export const CreateAIContentCarousel: FC = () => {
  return (
    <Box
      sx={{
        p: 4,
        background: "linear-gradient(to right, #0D1035 20%, #81499C)",
      }}
    >
      <>
        <Box sx={{ pb: 2 }}>
          <Typography fontWeight={700} variant="body2" color="white">
            {t("Create content instantly with AI!")}
          </Typography>
          <Typography fontWeight={400} variant="body2" color="white">
            {t(
              "What will you create today? From striking logos, to posters, video call backgrounds and FAQs, your next masterpiece is just a few clicks away!",
            )}
          </Typography>
        </Box>
        <LaunchAIContentCardsCarousel analyticsIdPrefix="homepage" />
      </>
    </Box>
  );
};

export const LaunchAIContentCardsCarousel: FC<{ analyticsIdPrefix: string }> = ({
  analyticsIdPrefix,
}) => (
  <Carousel
    items={[
      {
        key: "video",
        title: "Video",
        image: "https://images.changeengine.com/video.png",
        format: "Video",
      },
      {
        key: "videocall",
        title: "Video Call Background",
        image: "https://images.changeengine.com/zoombg2.png",
        format: "Video Call Background",
      },
      {
        key: "onepager",
        title: "Manager One-Pager",
        image: "https://images.changeengine.com/onepager2.png",
        format: "Manager One-Pager",
      },
      {
        key: "banner",
        title: "Horizontal Banner",
        image: "https://images.changeengine.com/horizontalbanner.png",
        format: "Horizontal Banner",
      },
      {
        key: "guide",
        title: "Guide",
        image: "https://images.changeengine.com/guide.png",
        format: "Guide",
      },
      {
        key: "squarelogo",
        title: "Square Logo",
        image: "https://images.changeengine.com/squarelogo2.png",
        format: "Square Logo",
      },
      {
        key: "landscapelogo",
        title: "Landscape Logo",
        image: "https://images.changeengine.com/landscapelogo2.png",
        format: "Landscape Logo",
      },
      {
        key: "tvdisplay",
        title: "TV Display",
        image: "https://images.changeengine.com/tvbg2.png",
        format: "TV Display",
      },
      {
        key: "poster",
        title: "Poster",
        image: "https://images.changeengine.com/poster2.png",
        format: "Poster",
      },
      {
        key: "certificate",
        title: "Certificate",
        image: "https://images.changeengine.com/certificate2.png",
        format: "Certificate",
      },
      {
        key: "faq",
        title: "FAQ",
        image: "https://images.changeengine.com/faq2.png",
        format: "FAQ",
      },
    ]}
    gap={2}
    Item={({ value: props }) => (
      <LaunchAIContentCard
        {...props}
        data-analytics-id={`${analyticsIdPrefix}-mfai-carousel-${props.key}`}
        key={props.key}
      />
    )}
    sx={(theme) => ({
      "--carousel-navigation-button-width": theme.spacing(6),
    })}
    itemWidth="160px"
  />
);

export const LaunchAIContentCard: FC<{
  title: string;
  format: string;
  image: string;
  "data-analytics-id"?: string;
}> = ({ title, format, image, "data-analytics-id": analyticsId }) => {
  const navigate = useNavigate();

  const handleClick = useCallback(() => {
    const params = new URLSearchParams({ format });

    void navigate({ pathname: "/create-content", search: params.toString() });
  }, [navigate, format]);

  return (
    <Box
      data-analytics-id={analyticsId}
      onClick={handleClick}
      sx={{
        maxWidth: "160px",
        background: "linear-gradient(to right bottom, #00B6EF, #C24599)",
        color: "white",
        borderRadius: "4px",
        cursor: "pointer",
        position: "relative",
      }}
    >
      <Box
        component="img"
        sx={{
          height: "65px",
          maxWidth: "160px",
          display: "block",
        }}
        alt={title}
        src={image}
      ></Box>
      <Box
        alignItems={"center"}
        justifyContent={"center"}
        display="flex"
        sx={{
          height: "35px",
          background: "linear-gradient(to right bottom, #00B6EF, #C24599)",
          width: "100%",
          maxWidth: "160px",
          borderRadius: "0 0 4px 4px",
        }}
      >
        <Typography variant="body2">{title}</Typography>
      </Box>
    </Box>
  );
};
